import { canv } from "@/canv/global";
import {
    getBoolProperty,
    getColorProperty,
    getNumberProperty,
    getStringProperty,
    isBrowser,
    lightOrDark,
} from "@/canv/utils";
import { elementBase } from "@/elementBase/elementBase";
import { textWidget } from "@/elements/text/text";
import type { Group } from "@/group";
import type { Area } from "@/group/types";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import type {
    FrameStateColor,
    FrameStateFixType,
    FrameStateFull,
    FrameStateHide,
    FrameStateMap,
    FrameStateMove,
    FrameStateResize,
} from "./types";

export class frameWidget extends elementBase {
    constructor(startX: number, startY: number) {
        super();
        this.startX = startX;
        this.startY = startY;
        this.width = 250;
        this.height = 250;

        this.textName = new textWidget();
        this.textName.setText("Frame");

        this.textName.menuTextSize(21, true);

        this.textName.menuTextColor("white", true);
        this.textName.setKeepMinWidth(false);
        this.textName.onlyLineMode = true;

        this.rotatable = false;

        this.updateDims();
    }

    updateDims() {
        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    // TryAction -----------------------------------------------

    TryAction(type: string) {
        this.textName.TryAction(type);
        this.updateDims();

        if (type === "keyboardDown") {
            this.tryShortcut();
        }

        if (type === "mousedown" && !this.isOver()) {
            const tool = canv.toolBox.TryGetToolByName("cursorTool");

            if (tool) {
                tool.turnMode("");
                tool.TryAction("mousedown");
            }
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "focus" && this.shouldFocusText()) {
            this.isNameEditing = true;
            this.fixState("nameFocus");
            this.textName.focusText();
            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        canv.eventManager.openRecord();
        this.saveState("nameFocus");
        canv.eventManager.closeRecord();

        this.textName.unfocusText();
        this.isNameEditing = false;
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            // stopInteraction вызывается внутри cursorTool при изменении mode
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    // --------------------------------------------------------------------

    // Utils

    getTitleProps() {
        // Считаем titleWidth, titleHeight. Меняем честный this.textName только если Focused, так
        // как менять его на каждый draw сильно есть производительность
        // Всегда просто рисуем текст через fillText, а когда Focused this.textName.draw()
        // getTitleProps всегда отдает то, что нарисовано

        const fontSize = canv.actionManager.getZoomConstant(16);
        const margin = canv.actionManager.getZoomConstant(7);
        const padding = canv.actionManager.getZoomConstant(9);
        const titleX = this.startX;
        let titleY = 0;
        let titleWidth = 0;
        let titleHeight = 0;
        let text = "";
        let isCutted = false;

        if (!this.isNameEditing) {
            text = this.textName.getText();

            canv.ctx.font = `${fontSize.toString()}px sans-serif`;
            const metrics = canv.ctx.measureText(text);
            titleHeight = metrics.actualBoundingBoxAscent; //+ metrics.actualBoundingBoxDescent;
            titleWidth = metrics.width;

            titleY = this.startY - margin - titleHeight - 2 * padding;

            isCutted = 2 * margin + 2 * padding + titleWidth >= this.width;

            if (isCutted) {
                const dotsMetrics = canv.ctx.measureText("...");
                const itemMetrics = canv.ctx.measureText("ш");

                // сбрасываем
                text = "";
                titleWidth = 0;

                for (let i = 0; i < this.textName.lines[0].items.length; i++) {
                    if (
                        titleWidth + dotsMetrics.width + itemMetrics.width >=
                        this.width - 2 * margin - 2 * padding
                    ) {
                        break;
                    }

                    text += this.textName.lines[0].items[i].symbol;
                    titleWidth += itemMetrics.width;
                }

                if (text) {
                    text += "...";
                    titleWidth += dotsMetrics.width;
                }
            }
        } else {
            text = this.textName.getText();
            this.textName.menuTextSize(fontSize, true);
            this.textName.updateDims();

            titleY = this.startY - margin - this.textName.height - 2 * padding;

            this.textName.startX = titleX + padding;
            this.textName.startY = titleY + padding + this.textName.height;
            this.textName.updateDims();

            titleWidth = this.textName.width;
            titleHeight = this.textName.height;
        }

        return {
            padding: padding,
            margin: margin,
            fontSize: fontSize,
            titleWidth: titleWidth,
            titleHeight: titleHeight,
            startX: titleX,
            startY: titleY,
            text: text,
            hidden: isCutted && text === "",
        };
    }

    // Draw

    colorShade(colorString: string, amt: number) {
        let col = colorString.replace(/^#/, "");
        if (col.length === 3) col = col[0] + col[0] + col[1] + col[1] + col[2] + col[2];

        let [r, g, b] = col.match(/.{2}/g) as [string, string, string];

        r = Math.max(Math.min(255, Number.parseInt(r, 16) + amt), 0).toString(16);
        g = Math.max(Math.min(255, Number.parseInt(g, 16) + amt), 0).toString(16);
        b = Math.max(Math.min(255, Number.parseInt(b, 16) + amt), 0).toString(16);

        const rr = (r.length < 2 ? "0" : "") + r;
        const gg = (g.length < 2 ? "0" : "") + g;
        const bb = (b.length < 2 ? "0" : "") + b;

        return `#${rr}${gg}${bb}`;
    }

    draw() {
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        canv.ctx.beginPath();

        // Сам фрейм
        let backColor = "white";

        if (this.color === "transparent") {
            backColor = "transparent";
        } else if (this.colorMap[this.color]) {
            backColor = this.colorMap[this.color];
        } else {
            backColor = this.colorShade(this.color, 80);
        }

        if (backColor !== "transparent") {
            canv.ctx.fillStyle = backColor;
            this.drawRoundedRect(
                this.startX,
                this.startY,
                this.width,
                this.height,
                canv.actionManager.getZoomConstant(4),
            );
            canv.ctx.fill();
        }
    }

    drawBorder() {
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        canv.ctx.beginPath();

        let borderColor = this.color;
        if (this.color === "transparent") {
            borderColor = "#dadada";
        }

        canv.ctx.strokeStyle = borderColor;
        this.drawRoundedRect(
            this.startX,
            this.startY,
            this.width,
            this.height,
            canv.actionManager.getZoomConstant(4),
        );
        canv.ctx.stroke();
    }

    drawTitle() {
        // title
        const titleProps = this.getTitleProps();

        const titleBackColor = this.color;
        let textColor = "white";

        if (lightOrDark(this.color) === "light") {
            textColor = "black";
        } else {
            textColor = "white";
        }

        if (!titleProps.hidden) {
            if (!this.isNameEditing) {
                // Рисуем placeholder, иначе тормозит
                canv.ctx.font = `${titleProps.fontSize.toString()}px sans-serif`;
                if (titleBackColor !== "transparent") {
                    canv.ctx.fillStyle = titleBackColor;
                    this.drawRoundedRect(
                        titleProps.startX,
                        titleProps.startY,
                        2 * titleProps.padding + titleProps.titleWidth,
                        2 * titleProps.padding + titleProps.titleHeight,
                        canv.actionManager.getZoomConstant(4),
                    );
                    canv.ctx.fill();
                }

                canv.ctx.fillStyle = textColor;
                canv.ctx.fillText(
                    titleProps.text,
                    titleProps.startX + titleProps.padding,
                    titleProps.startY + titleProps.padding + titleProps.titleHeight,
                );
            } else {
                if (titleBackColor !== "transparent") {
                    canv.ctx.fillStyle = titleBackColor;
                    this.drawRoundedRect(
                        titleProps.startX,
                        titleProps.startY,
                        2 * titleProps.padding + titleProps.titleWidth,
                        2 * titleProps.padding + titleProps.titleHeight,
                        canv.actionManager.getZoomConstant(4),
                    );
                    canv.ctx.fill();
                }

                if (lightOrDark(this.color) === "light") {
                    this.textName.menuTextColor("black", true);
                } else {
                    this.textName.menuTextColor("white", true);
                }

                this.textName.draw();
            }
        }
    }

    drawOver() {
        this.drawBorder();
        this.drawTitle();

        if (!this.isHidden) {
            return;
        }

        canv.ctx.fillStyle = "#F5F5F5";
        this.drawRoundedRect(
            this.startX,
            this.startY,
            this.width,
            this.height,
            canv.actionManager.getZoomConstant(4),
        );
        canv.ctx.fill();

        const lineWidth = canv.actionManager.getConstant(15);
        const linesAmount = (this.width - lineWidth) / 2 / lineWidth;
        let posX = this.startX + lineWidth;

        for (let i = 0; i < linesAmount; i++) {
            let drawWidth = lineWidth;
            if (posX + lineWidth > this.startX + this.width) {
                drawWidth = this.startX + this.width - posX;
            }
            canv.ctx.fillStyle = "#E6E6E6";
            this.drawRoundedRect(
                posX,
                this.startY,
                drawWidth,
                this.height,
                canv.actionManager.getZoomConstant(4),
            );
            canv.ctx.fill();

            posX += 2 * lineWidth;
        }
    }

    // Helpers

    isOverVerticalLine(lineX: number, startY: number, endY: number) {
        const topY = startY < endY ? startY : endY;
        const bottomY = startY > endY ? startY : endY;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return x > lineX - 2 - gap && x < lineX + 2 + gap && y > topY && y < bottomY;
    }

    isOverHorizontalLine(lineY: number, startX: number, endX: number) {
        const leftX = startX < endX ? startX : endX;
        const rightX = startX > endX ? startX : endX;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return x > leftX && x < rightX && y > lineY - 2 - gap && y < lineY + 2 + gap;
    }

    drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
        if (w < 0 || h < 0 || r < 0) {
            return;
        }
        let radius = r;
        if (w < 2 * r) radius = w / 2;
        if (h < 2 * r) radius = h / 2;
        canv.ctx.beginPath();
        canv.ctx.moveTo(x + radius, y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, radius);
        canv.ctx.arcTo(x + w, y + h, x, y + h, radius);
        canv.ctx.arcTo(x, y + h, x, y, radius);
        canv.ctx.arcTo(x, y, x + w, y, radius);
        canv.ctx.closePath();
    }

    isInArea(area: Area) {
        // Проверяем, что все углы элемента внутри области
        return (
            area.x <= this.baseStartX &&
            area.y <= this.baseStartY &&
            area.x + area.width >= this.baseStartX + this.baseWidth &&
            area.y + area.height >= this.baseStartY + this.baseHeight
        );
    }

    isOver(isCandidate?: boolean) {
        return !!this.getOverArea(isCandidate);
    }

    getOverArea(isCandidate?: boolean) {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const titleProps = this.getTitleProps();
        if (!titleProps.hidden) {
            if (
                x > titleProps.startX &&
                x < titleProps.startX + titleProps.titleWidth + 2 * titleProps.padding &&
                y > titleProps.startY &&
                y < titleProps.startY + titleProps.titleHeight + 2 * titleProps.padding
            ) {
                return "title";
            }
        }

        // Если фрейм находится под виджетом, то его можно выбрать только кликнув по тайтлу
        if (isCandidate) return "";

        const leftBorder = this.isOverVerticalLine(
            this.startX,
            this.startY,
            this.startY + this.height,
        );
        const rightBorder = this.isOverVerticalLine(
            this.startX + this.width,
            this.startY,
            this.startY + this.height,
        );

        const topBorder = this.isOverHorizontalLine(
            this.startY,
            this.startX,
            this.startX + this.width,
        );
        const bottomBorder = this.isOverHorizontalLine(
            this.startY + this.height,
            this.startX,
            this.startX + this.width,
        );

        if (leftBorder || rightBorder || topBorder || bottomBorder) {
            return "border";
        }

        return x > this.baseStartX &&
            x < this.baseStartX + this.baseWidth &&
            y > this.baseStartY &&
            y < this.baseStartY + this.baseHeight
            ? "body"
            : "";
    }

    shouldFocusText() {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const titleProps = this.getTitleProps();

        if (titleProps.hidden) {
            return false;
        }

        if (
            x > titleProps.startX &&
            x < titleProps.startX + titleProps.titleWidth + 2 * titleProps.padding
        ) {
            if (
                y > titleProps.startY &&
                y < titleProps.startY + titleProps.titleHeight + 2 * titleProps.padding
            ) {
                return true;
            }
        }

        return false;
    }

    searchText(text: string) {
        const frameTextName = this.textName.getText().toLowerCase();

        if (frameTextName.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this.textName,
                    mode: "widget",
                },
            ];
        }

        return [];
    }

    getOverlappedGroups() {
        return this.overlappedGroups;
    }

    getArea() {
        return {
            x: this.startX,
            y: this.startY,
            width: this.width,
            height: this.height,
        };
    }

    getFramesOnTopOfCurrent(area: Area) {
        let foundCurrent = false;
        const groupsFramesOnTopOfCurrent: Array<frameWidget> = [];

        for (const item of canv.canvasController.items) {
            for (const child of item.children) {
                // ищем только фреймы, которые вложены в текущий
                if (child instanceof frameWidget && item.isAnyItemInArea(area)) {
                    if (!foundCurrent) {
                        if (child.UID === this.UID) {
                            foundCurrent = true;
                        }
                    } else {
                        groupsFramesOnTopOfCurrent.push(child);
                    }
                }
            }
        }

        return groupsFramesOnTopOfCurrent;
    }

    calculateOverlappedGroups(isNotUseSelected = false) {
        this.overlappedGroups = [];

        const area = this.getArea();
        const framesOnTopOfCurrent = this.getFramesOnTopOfCurrent(area);
        const selectedUIDs = new Set(canv.canvasController.selected.map((s) => s.UID));

        for (const item of canv.canvasController.items) {
            // Пропускаем элемент, если он содержит подгруппу
            if (item.hasSubGroup(this.parent)) continue;

            // Если элемент не находится в заданной области, пропускаем его
            if (!item.isAnyItemInArea(area)) continue;

            // Если элемент находится в области одного из вложенных фреймов, пропускаем его
            if (framesOnTopOfCurrent.some((frame) => item.isAnyItemInArea(frame.getArea())))
                continue;

            if (
                this.parent &&
                // this.parent.parent == null &&
                this.parent.indexByParent < item.indexByParent
            ) {
                if (item.parent) {
                    // Если родитель элемента уже выбран, пропускаем его
                    if (
                        selectedUIDs.has(item.parentUID) ||
                        this.overlappedGroups.some((g) => g.UID === item.parentUID)
                    ) {
                        continue;
                    }

                    // Если элемент уже выбран, пропускаем его
                    if (!isNotUseSelected && selectedUIDs.has(item.UID)) continue;

                    item.parent.fixWidgets("move");
                    this.overlappedGroups.push(item.parent);
                } else {
                    // Если элемент уже выбран, пропускаем его
                    if (selectedUIDs.has(item.UID) || selectedUIDs.has(item.parentUID)) continue;

                    item.fixWidgets("move");
                    this.overlappedGroups.push(item);
                }
            }
        }
    }

    translatingStart() {
        this.shouldMoveOverlaped = true;
        this.calculateOverlappedGroups();
    }

    translatingStop() {
        for (let i = 0; i < this.overlappedGroups.length; i++) {
            this.overlappedGroups[i].saveWidgets("move");
        }

        this.shouldMoveOverlaped = false;
        this.overlappedGroups = [];
    }

    rotatingStart() {
        this.shouldMoveOverlaped = true;
        this.rotablesWidgetsUids = [];

        const area = this.getArea();
        const framesOnTopOfCurrent = this.getFramesOnTopOfCurrent(area);

        for (const item of canv.canvasController.items) {
            // Пропускаем элемент, если он содержит подгруппу
            if (item.hasSubGroup(this.parent)) continue;

            // Если элемент не находится в заданной области, пропускаем его
            if (!item.isAnyItemInArea(area)) continue;

            // Если элемент находится в области одного из вложенных фреймов, пропускаем его
            if (framesOnTopOfCurrent.some((frame) => item.isAnyItemInArea(frame.getArea())))
                continue;

            if (
                this.parent &&
                this.parent.parent == null &&
                this.parent.indexByParent < item.indexByParent
            ) {
                item.fixWidgets("move");
                this.overlappedGroups.push(item);

                // Выключаем переворот виджета и запоминаем UID если он в фрейме
                const child = item.onlyChild();
                if (child?.rotatable) {
                    child.rotatable = false;
                    this.rotablesWidgetsUids.push(item.UID);
                }
            }
        }
    }

    rotatingStop() {
        const groups = this.getOverlappedGroups();
        for (const item of groups) {
            item.saveWidgets("move");

            // Восстанавливаем возможность переворота виджета в фрейме
            if (this.rotablesWidgetsUids.includes(item.UID)) {
                const child = item.onlyChild();
                if (child) {
                    child.rotatable = true;
                }
            }
        }

        this.shouldMoveOverlaped = false;
        this.overlappedGroups = [];
        this.rotablesWidgetsUids = [];
    }

    updateAfterMobileEdit() {
        canv.eventManager.openRecord();
        this.fixState("full");
        this.updateDims();
        this.saveState("full");
        canv.eventManager.closeRecord();
    }
    // translate resize

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();

        if (!this.shouldMoveOverlaped) return;
        for (let i = 0; i < this.overlappedGroups.length; i++) {
            this.overlappedGroups[i].translateGroup(x, y);
        }
    }

    resize(x: number, y: number, catchPointer: string) {
        this.width += x;
        this.height += y;
        this.updateDims();
    }

    // Обработчики widget menu

    menuColor(color: string) {
        this.color = color;
        if (lightOrDark(this.color) === "light") {
            this.textName.menuTextColor("black", true);
        } else {
            this.textName.menuTextColor("white", true);
        }
    }

    menuHide(mode: boolean) {
        this.isHidden = mode;
    }

    // widget menu

    getWidgetOptions(isLocked?: boolean) {
        const widgetOptions: widgetOption[] = [];

        if (!isLocked) {
            widgetOptions.push(new widgetOption("color", this.color, this.UID));
        }
        if ((canv.isTeamMember() && canv.isBoardModeEditOrOwner()) || canv.isBoardModeOwner()) {
            widgetOptions.push(new widgetOption("hide", this.isHidden, this.UID));
        }
        this.textName.setEditTextMobile(widgetOptions, this.UID);

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        }
        if (name === "hide") {
            return this.menuHide;
        }
        if (name === "mobileEdit") {
            return this.textName.menuMobileEdit;
        }
    }

    // Далее для statesManager

    getFixType(reason: string): FrameStateFixType {
        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            case "wm_color":
                return "color";
            case "wm_hide":
                return "hide";
            default:
                return "full";
        }
    }

    getState<T extends FrameStateFixType>(fixType: T): FrameStateMap[T] {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as FrameStateMap[T];
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                } as FrameStateMap[T];
            case "color":
                return {
                    color: this.color,
                } as FrameStateMap[T];
            case "hide":
                return {
                    isHidden: this.isHidden,
                } as FrameStateMap[T];
            default:
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "frameWidget",
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    color: this.color,
                    isHidden: this.isHidden,
                    textName: this.textName.getState("full"),
                } as FrameStateMap[T];
        }
    }

    loadState<T extends FrameStateFixType>(stateObj: FrameStateMap[T], fixType: T): void {
        if (fixType === "full") {
            const state = stateObj as FrameStateFull;
            this.UID = getStringProperty("UID", state);
            this.parentUID = getStringProperty("parentUID", state);
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
            this.color = getColorProperty("color", state);
            this.isHidden = getBoolProperty("isHidden", state, false);
            this.textName.loadState(state.textName, "full");

            if (isBrowser()) {
                if (lightOrDark(this.color) === "light") {
                    this.textName.fontColor = "black";
                } else {
                    this.textName.fontColor = "white";
                }
            }
        } else if (fixType === "move") {
            const state = stateObj as FrameStateMove;
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
        } else if (fixType === "resize") {
            const state = stateObj as FrameStateResize;
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
        } else if (fixType === "color") {
            const state = stateObj as FrameStateColor;
            this.color = getColorProperty("color", state);
        } else if (fixType === "hide") {
            const state = stateObj as FrameStateHide;
            this.isHidden = getBoolProperty("isHidden", state, false);
        }

        // Именно два раза
        this.updateDims();
    }

    constructorName = "frame";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    textName: textWidget;

    color = "#0c8de9";
    isHidden = false;

    shouldMoveOverlaped = false;
    overlappedGroups: Group[] = [];
    isNameEditing = false;
    rotablesWidgetsUids: string[] = [];
    allowOptionsForLocked = true;

    colorMap = {
        "#111111": "#F4F4F4",
        "#E03E1A": "#F9E4DF",
        "#f79722": "#FDF2E1",
        "#f3c11b": "#FEF7E1",
        "#198f51": "#E0F2E7",
        "#0c8de9": "#DFEFFD",
        "#8a38f5": "#EDE4FD",
        "#ffffff": "#ffffff",

        "#383838": "#F4F4F4",
        "#d9d9d9": "#F4F4F4",
        "#fbbcb6": "#F9E4DF",
        "#ffc470": "#FDF2E1",
        "#f9df90": "#FEF7E1",
        "#a1e8b9": "#E0F2E7",
        "#a8d7fa": "#DFEFFD",
        "#d6b6fb": "#EDE4FD",
    };
}
